import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Sparkles } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * `/r/:code` — catches creator referral links.
 * Drops `dg_creator_ref` cookie server-side + shows a friendly splash
 * before redirecting to the landing page.
 */
export default function CreatorReferralCatcher() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const resolve = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/creators/r/${encodeURIComponent(code || "")}`, {
          withCredentials: true,
        });
        setCreator(r.data);
        // Persist locally too (cookie is set by backend but expose to frontend flows)
        try { localStorage.setItem("dg_creator_ref", (code || "").toUpperCase()); } catch { /* ignore quota */ }
        setTimeout(() => navigate("/", { replace: true }), 1800);
      } catch {
        setError("Invalid or expired referral link");
        // Log the failed lookup so monitoring can pick it up — still redirects gracefully
        try {
          const bc = process.env.REACT_APP_BACKEND_URL;
          if (bc) {
            navigator.sendBeacon?.(
              `${bc}/api/creators/r/${encodeURIComponent(code || "")}/miss`,
              ""
            );
          }
        } catch (beaconErr) {
          console.warn('[referral] miss beacon failed:', beaconErr);
        }
        setTimeout(() => navigate("/", { replace: true }), 2000);
      }
    };
    resolve();
  }, [code, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1E1B4B] via-[#3B0764] to-[#E83E8C] text-white p-6" data-testid="referral-catcher">
      <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 max-w-sm w-full text-center border border-white/20">
        <Sparkles size={40} className="mx-auto mb-3 text-[#FF9F1C]" />
        {error ? (
          <p className="text-sm text-white/80" data-testid="referral-error">{error}</p>
        ) : creator ? (
          <>
            <p className="text-xs uppercase tracking-widest text-[#FF9F1C]">Invited by</p>
            <h1 className="text-2xl font-extrabold mt-1" data-testid="referral-creator-name">{creator.creator_name || "a DoozyGo Creator"}</h1>
            <p className="text-xs text-white/70 mt-3">Sending you to the party zone…</p>
            <Loader2 size={18} className="animate-spin mx-auto mt-3" />
          </>
        ) : (
          <Loader2 size={18} className="animate-spin mx-auto" />
        )}
      </div>
    </div>
  );
}
